import { useState, useEffect } from 'react';
import { supabase } from '@cleanpro/common-ui';

export interface ActiveBusinessUrl {
  id: string;
  business_name: string;
  slug: string;
  url: string;
}

export function useActiveBusinessUrls() {
  const [businesses, setBusinesses] = useState<ActiveBusinessUrl[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchActiveBusinessUrls = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: queryError } = await supabase
        .from('businesses')
        .select('id, business_name, slug')
        .eq('landing_page_status', 'active')
        .eq('has_website_feature', true)
        .order('business_name', { ascending: true });

      if (queryError) {
        throw new Error(`Failed to fetch active businesses: ${queryError.message}`);
      }

      if (!data) {
        setBusinesses([]);
        return;
      }

      const businessUrls = data.map(b => ({
        id: b.id,
        business_name: b.business_name,
        slug: b.slug,
        url: `${import.meta.env.VITE_MARKETING_URL || ''}/service-providers/${b.slug}`,
      }));

      setBusinesses(businessUrls);
    } catch (err) {
      console.error('[useActiveBusinessUrls] Error fetching active business URLs:', err);
      setError(err instanceof Error ? err : new Error('Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActiveBusinessUrls();

    // Subscribe to changes in businesses table for real-time updates
    const subscription = supabase
      .channel('active-business-urls-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'businesses',
          filter: 'landing_page_status=eq.active',
        },
        () => {
          console.log('[useActiveBusinessUrls] Business change detected, refreshing URLs');
          fetchActiveBusinessUrls();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return {
    businesses,
    loading,
    error,
    refresh: fetchActiveBusinessUrls,
  };
}
